import React, { useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  AppBar,
  Box,
  Link,
  Menu,
  Toolbar,
  Typography,
  Button,
  Avatar,
  IconButton,
} from "@material-ui/core";
import Brightness4OutlinedIcon from "@material-ui/icons/Brightness4Outlined";
import Brightness7OutlinedIcon from "@material-ui/icons/Brightness7Outlined";
import { Actions as LangActions } from "../../../store/ducks/i18n";
import { useLocalStorage } from "../../../common/utils";
import Logo from "../Logo";
import pt from "../../../resources/i18n/pt.svg";
import en from "../../../resources/i18n/en.svg";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import SelectionSessionImportButton from "../core/SelectionSessionImportButton";
import { useThemeMode } from "../../contexts/index.jsx";
import { followsThemeMode } from "../../pages/Shoreline/theme.js";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  title: {
    flexGrow: 1,
    fontWeight: 700,
  },
  brand: {
    display: "inline-block",
    "&:hover": {
      textDecoration: "none",
    },
  },
  real_brand: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
  },
  manualButton: {
    marginRight: theme.spacing(2),
  },
  flag: {
    width: "20px",
    height: "20px",
    marginLeft: "10px",
  },
}));

const manualLinks = {
  main: {
    "pt-BR": "https://drive.google.com/file/d/1jr5k0Msgvuy-qMryFDxHRLo3_jiDQ2Vd/view?usp=sharing",
    "en-US": "https://drive.google.com/file/d/1wZ-0QSdwRXifyGloouHbo2AjlzTVmUGW/view?usp=sharing",
  },
  acquisition: {
    "pt-BR":
      "https://drive.google.com/file/d/1vBzYyyD5wvLUJiI7EU6_VRItdXkZfF-z/view?usp=sharing",
    "en-US":
      "https://drive.google.com/file/d/1rPTyhF0YxJxooMtfiMRgRx3gt1Y2G2Jv/view?usp=sharing",
  },
  bathymetry: {
    "pt-BR":
      "https://drive.google.com/file/d/1lHzY5KMZjElaLLC20CwGRR_5c14kA0f2/view?usp=sharing",
    "en-US":
      "https://drive.google.com/file/d/1zyw74TB_fIBJL3iSS3JrJkEDJn1hduzp/view?usp=sharing",
  },
  csqueeze: {
    "pt-BR":
      "https://drive.google.com/file/d/14xEpfZf3tKbjJhhSrL0CQ_4GF_I0b6Fl/view?usp=sharing",
    "en-US":
      "https://drive.google.com/file/d/1Q7JsWK355_H9cm4rsVwET62vy1VYJBXn/view?usp=sharing",
  },
  intertidal: {
    "pt-BR":
      "https://drive.google.com/file/d/1gIG0VEC1FAAN6abvTKoKuO-H7nZoWZia/view?usp=sharing",
    "en-US":
      "https://drive.google.com/file/d/1bBTNFnlJ2iy0JC0jI8KeW2AodSIIdKox/view?usp=sharing",
  },
  mepbay: {
    "pt-BR":
      "https://drive.google.com/file/d/1PMyyVhZnSTzCeU-YtfjFIl0VFuk0pCfY/view?usp=sharing ",
    "en-US":
      "https://drive.google.com/file/d/1y5aScjpfnHi_0coSP8xBRoftmA4Vvp6C/view?usp=sharing",
  },
};

const pathMap = {
  "/main/acquisition": "acquisition",
  "/main/bathymetry": "bathymetry",
  "/main/csqueeze": "csqueeze",
  "/main/intertidal": "intertidal",
  "/main/mepbay": "mepbay",
  "/main": "main",
};


export default function NavBar(props) {
  const classes = useStyles();
  const [t] = useTranslation();
  const [anchor, setAnchor] = useState(null);
  const dispatch = useDispatch();
  const [language, setLanguage] = useLocalStorage("selectedLanguage", "pt-BR");
  const user = useSelector((state) => state.auth.user, shallowEqual);
  const location = useLocation();
  const { shorelineThemeMode, toggleShorelineThemeMode } = useThemeMode();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";

  const handleLanguageChange = (local) => {
    dispatch(LangActions.setLang(local));
    setLanguage(local);
    setAnchor(null);
  };

  const pathname = location?.pathname || "";

  const matchedPath = Object.keys(pathMap)
  .sort((a, b) => b.length - a.length)
  .find(
    (key) => pathname === key || pathname.startsWith(key + "/")
  );

  const manualKey = matchedPath ? pathMap[matchedPath] : null;
  const isSelectionPage = pathname === "/main/selection";
  //console.log("[MANUAL] Manual key:", manualKey);

  const fileLink =
    manualKey && manualLinks[manualKey]
      ? manualLinks[manualKey][language]
      : null;

  //console.log("[MANUAL] Selected language:", language);
  //console.log("[MANUAL] Final file link:", fileLink);

  return (
    <div className={classes.root}>
      <AppBar position="fixed" color="default" style={isDark ? { backgroundColor: theme.palette.background.paper } : undefined}>
        <Toolbar style={{ justifyContent: "space-between" }}>
          <Box className={classes.title}>
            <Link color="inherit" href="/" className={classes.brand}>
              <Typography
                variant="h6"
                color="initial"
                className={classes.real_brand}
              >
                <Logo kind={isDark ? "outline" : "filled"} /> {t("self.title")}
              </Typography>
            </Link>
          </Box>

          <div style={{ display: "flex", alignItems: "center" }}>
            {isSelectionPage && <SelectionSessionImportButton />}

            {fileLink && (
              <Button
                className={classes.manualButton}
                variant="outlined"
                color="primary"
                href={fileLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("home.instructions.btnManual")}
              </Button>
            )}

            {followsThemeMode(pathname) && (
              <IconButton onClick={toggleShorelineThemeMode} title={t("forms.shorelineAnalysis.toggleTheme")}>
                {shorelineThemeMode === "dark" ? <Brightness7OutlinedIcon /> : <Brightness4OutlinedIcon />}
              </IconButton>
            )}

            <Button onClick={() => handleLanguageChange("pt-BR")}>
              <Avatar alt="Português" src={pt} className={classes.flag} />
              pt-BR
            </Button>

            <Button onClick={() => handleLanguageChange("en-US")}>
              <Avatar alt="English" src={en} className={classes.flag} />
              en-US
            </Button>
          </div>
        </Toolbar>

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          getContentAnchorEl={null}
        >
          {props.children}
        </Menu>
      </AppBar>
    </div>
  );
}

